import React, { useRef, useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, ListGroup, Modal } from 'react-bootstrap';
import { Link, useRouteMatch, useHistory } from 'react-router-dom';
import '../components/components.css';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faWaveSquare,
  faVolumeUp,
  faMagic,
  faClock,
  faMicrophone,
  faGuitar,
  faPenNib,
  faFolderOpen,
  faUpload,
  faMusic,
  faPlay,
  faPlus,
  faDesktop,
  faFolder,
  faTimes
} from "@fortawesome/free-solid-svg-icons";
import { useAudio } from "../context/AudioContext";
import { useMediaQuery } from "react-responsive";
import { createProject, uploadProjectAsset, getProjects } from '../../../service/beats-api';

// Import audio files
import oneAudio from '../../../one.mp3';
import twoAudio from '../../../two.mp3';
import threeAudio from '../../../three.mp3';
import fourAudio from '../../../four.mp3';
import fiveAudio from '../../../five.mp3';

const BeatsHome = () => {
  const url = useRouteMatch().url;
  const history = useHistory();
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFileName, setSelectedFileName] = useState('');
  const [showCreateProject, setShowCreateProject] = useState(false);
  const [projectName, setProjectName] = useState('');
  const [projectDescription, setProjectDescription] = useState('');
  const [projectTags, setProjectTags] = useState('');
  const [isCreatingProject, setIsCreatingProject] = useState(false);
  const [recentProjects, setRecentProjects] = useState([]);
  const [selectedSampleTrack, setSelectedSampleTrack] = useState(null);
  const { playAudio } = useAudio();
  const isTabletOrMobile = useMediaQuery({ query: "(max-width: 1024px)" });
  
  // Sample music tracks
  const sampleTracks = [
    {
      id: 1,
      name: "Spring Mornings",
      type: "Audio",
      size: "3.5 MB",
      src: oneAudio
    },
    {
      id: 2,
      name: "Jesus Hold my Hands",
      type: "Audio",
      size: "4.2 MB",
      src: twoAudio
    },
    {
      id: 3,
      name: "Light a Match",
      type: "Audio",
      size: "3.8 MB",
      src: threeAudio
    },
    {
      id: 4,
      name: "Christmas Cheer",
      type: "Audio",
      size: "2.9 MB",
      src: fourAudio
    },
    {
      id: 5,
      name: "Needle in the Silence",
      type: "Audio",
      size: "2.9 MB",
      src: fiveAudio
    }
    
  ];
  
  const handleFileInputChange = (e) => {
    if (e.target.files.length > 0) {
      const file = e.target.files[0];
      setSelectedFileName(file.name);
      setSelectedFile(file);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current.click();
  };
  
  const handlePlayUploadedFile = () => {
    if (selectedFile) {
      const objectUrl = URL.createObjectURL(selectedFile);
      playAudio(objectUrl, selectedFileName);
    }
  };
  
  const handlePlaySample = (track) => {
    playAudio(track.src, track.name);
  };

  const handleCreateProject = async () => {
    if (!projectName.trim()) return;

    console.log('=== Starting project creation ===');
    console.log('selectedFile:', selectedFile);
    console.log('selectedFileName:', selectedFileName);
    console.log('selectedSampleTrack:', selectedSampleTrack);
    console.log('projectName:', projectName);

    // Fix: Make sure we have the correct selectedSampleTrack value
    const currentSampleTrack = selectedSampleTrack;
    if (currentSampleTrack && typeof currentSampleTrack === 'object' && currentSampleTrack._reactName) {
      console.error('selectedSampleTrack is an event object, not a track object!');
      alert('There was an error with the sample track selection. Please try again.');
      return;
    }

    setIsCreatingProject(true);
    
    try {
      // Prepare project data
      const projectData = {
        name: projectName.trim(),
        description: projectDescription.trim(),
        tags: projectTags.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0),
        status: 'IN_PROGRESS'
      };

      // Create the project
      const response = await createProject(projectData);
      
      if (response.success) {
        const newProject = response.project;
        
        // Upload audio file if one is selected
        if (selectedFile) {
          try {
            console.log('Uploading selected file:', selectedFile.name, selectedFile.size, 'bytes');
            const uploadResponse = await uploadProjectAsset(newProject.id, selectedFile, 'ORIGINAL');
            console.log('Upload response:', uploadResponse);
            
            if (!uploadResponse || !uploadResponse.success) {
              throw new Error(uploadResponse?.error || 'Failed to upload file');
            }
            
            console.log('File uploaded successfully!');
          } catch (uploadError) {
            console.error('Error uploading file:', uploadError);
            alert(`Project created but file upload failed: ${uploadError.message}. You can upload the file manually from the project page.`);
          }
        } else if (currentSampleTrack) {
          // Handle sample track upload
          try {
            console.log('Uploading sample track:', currentSampleTrack?.name || 'Unknown');
            console.log('Selected sample track object:', currentSampleTrack);
            
            if (!currentSampleTrack || !currentSampleTrack.src) {
              throw new Error('Invalid sample track selected');
            }
            
            const audioResponse = await fetch(currentSampleTrack.src);
            if (!audioResponse.ok) {
              throw new Error(`Failed to fetch sample audio: ${audioResponse.statusText}`);
            }
            
            const audioBlob = await audioResponse.blob();
            const fileName = currentSampleTrack.name ? `${currentSampleTrack.name}.mp3` : `sample_${Date.now()}.mp3`;
            const audioFile = new File([audioBlob], fileName, { type: 'audio/mpeg' });
            
            console.log('Created audio file:', audioFile.name, audioFile.size, 'bytes');
            
            const uploadResponse = await uploadProjectAsset(newProject.id, audioFile, 'ORIGINAL');
            console.log('Sample upload response:', uploadResponse);
            
            if (!uploadResponse || !uploadResponse.success) {
              throw new Error(uploadResponse?.error || 'Failed to upload sample track');
            }
            
            console.log('Sample track uploaded successfully!');
          } catch (uploadError) {
            console.error('Error uploading sample track:', uploadError);
            alert(`Project created but sample track upload failed: ${uploadError.message}. You can upload a file manually from the project page.`);
          }
        }

        // Reset form
        setProjectName('');
        setProjectDescription('');
        setProjectTags('');
        setSelectedFile(null);
        setSelectedFileName('');
        setSelectedSampleTrack(null);
        setShowCreateProject(false);
        
        // Navigate to project page
        history.push(`${url}/project/${newProject.id}`);
      } else {
        alert('Failed to create project: ' + (response.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error creating project:', error);
      alert('Failed to create project. Please try again.');
    } finally {
      setIsCreatingProject(false);
    }
  };

  const handleShowCreateProject = (sampleTrack = null) => {
    console.log('handleShowCreateProject called with:', sampleTrack);
    console.log('Current selectedFile:', selectedFile);
    console.log('Current selectedFileName:', selectedFileName);
    
    if (selectedFile || selectedFileName || sampleTrack) {
      if (sampleTrack) {
        console.log('Setting selectedSampleTrack to:', sampleTrack);
        setSelectedSampleTrack(sampleTrack);
        setSelectedFile(null);
        setSelectedFileName('');
      }
      setShowCreateProject(true);
    } else {
      console.log('No file or sample track provided, not showing modal');
    }
  };

  const resetProjectForm = () => {
    setProjectName('');
    setProjectDescription('');
    setProjectTags('');
    setSelectedFile(null);
    setSelectedFileName('');
    setSelectedSampleTrack(null);
    setShowCreateProject(false);
  };

  // Debug: Monitor selectedSampleTrack changes
  useEffect(() => {
    console.log('selectedSampleTrack changed to:', selectedSampleTrack);
    if (selectedSampleTrack && typeof selectedSampleTrack === 'object' && selectedSampleTrack._reactName) {
      console.error('ERROR: selectedSampleTrack is an event object!', selectedSampleTrack);
    }
  }, [selectedSampleTrack]);

  // Load recent projects on component mount
  useEffect(() => {
    loadRecentProjects();
  }, []);

  const loadRecentProjects = async () => {
    try {
      const response = await getProjects();
      if (response.success) {
        // Take only the 5 most recent projects
        setRecentProjects(response.projects.slice(0, 5));
      }
    } catch (error) {
      console.error('Error loading recent projects:', error);
    }
  };

  return (
    // <Container fluid className="beats-component-container" style={{ minHeight: '100vh' }}>
      <>
      <Row className="h-100">
        {/* Main Content - Full Width */}
        <Col xs={12} className="p-3">
          {/* Top Navigation Buttons */}
          <Row className="mb-4">
            <Col>
              <div className="d-flex align-items-center justify-content-between flex-wrap">
                <div className="mb-2 mb-md-0">
                  <h1 className="welcome-text mb-2">Welcome to <span className="beats-gradient">Beats!</span></h1>
                  <p className="text-secondary-light mb-0">Your AI-powered audio production assistant</p>
                </div>
                <div className="d-flex gap-2 flex-wrap">
                  <Button 
                    as={Link}
                    to={`${url}/workspace`}
                    variant="outline-light"
                    className="beats-btn-outline"
                    style={{ minWidth: '120px' }}
                  >
                    <FontAwesomeIcon icon={faDesktop} className="me-2" />
                    Workspace
                  </Button>
                  <Button 
                    as={Link}
                    to={`${url}/my-library`}
                    className="beats-btn-purple"
                    style={{ minWidth: '130px' }}
                  >
                    <FontAwesomeIcon icon={faFolder} className="me-2" />
                    My Library
                  </Button>
                </div>
              </div>
            </Col>
          </Row>
          
          <Row className="gx-4 h-75">
            {/* Left Pane - Audio Upload */}
            <Col md={6}>
              <Card className="beats-card h-100 d-flex flex-column">
                <Card.Body className="d-flex flex-column">
                  <div className="mb-4 flex-grow-1">
                    <h5 className="mb-3" style={{ color: '#d9d9d9' }}>Upload your audio file to proceed</h5>
                    
                    {/* Hidden file input */}
                    <input 
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileInputChange}
                      className="d-none"
                      accept="audio/*"
                    />
                    
                    <div className="text-center p-4 mb-3" 
                      style={{ 
                        border: '2px dashed var(--border-color)', 
                        borderRadius: '8px', 
                        backgroundColor: 'rgba(0,0,0,0.2)',
                        cursor: 'pointer'
                      }}
                      onClick={handleUploadClick}
                    >
                      <FontAwesomeIcon 
                        icon={faUpload} 
                        style={{ fontSize: 'var(--font-4xl)', color: 'var(--purple-accent)', marginBottom: '1rem' }} 
                      />
                      <div className="mb-2">
                        {selectedFileName ? (
                          <p className="mb-0" style={{ color: '#d9d9d9' }}>{selectedFileName}</p>
                        ) : (
                          <p className="mb-0" style={{ color: '#d9d9d9' }}>Click to select an audio file</p>
                        )}
                      </div>
                      <p className="beats-form-text mb-0">
                        Supported formats: MP3, WAV, FLAC (Max 20MB)
                      </p>
                    </div>
                    <div className="d-flex gap-2">
                      <Button 
                        className="beats-btn-purple flex-grow-1" 
                        disabled={!selectedFileName}
                        onClick={() => handleShowCreateProject()}
                      >
                        <FontAwesomeIcon icon={selectedFileName ? faPlus : faUpload} className="me-2" />
                        {selectedFileName ? 'Create Project' : 'Select a file first'}
                      </Button>
                      
                      {selectedFileName && (
                        <Button 
                          className="beats-btn-outline"
                          onClick={handlePlayUploadedFile}
                        >
                          <FontAwesomeIcon icon={faPlay} />
                        </Button>
                      )}
                    </div>
                  </div>
                  
                  <div className="mt-auto">
                    <h5 className="mb-3" style={{ color: '#d9d9d9' }}>Demo Music to Test</h5>
                    <Card className="beats-card">
                      <Card.Body>
                        <ListGroup variant="flush">
                          {sampleTracks.map((track) => (
                            <ListGroup.Item key={track.id} className="beats-list-item d-flex justify-content-between align-items-center">
                              <div className="d-flex align-items-center">
                                <FontAwesomeIcon icon={faMusic} className="me-3" />
                                <div>
                                  <h6 className="mb-0" style={{ color: '#d9d9d9' }}>{track.name}</h6>
                                  <small className="text-secondary">{track.type} • {track.size}</small>
                                </div>
                              </div>
                              <div className="d-flex gap-2">
                                <button 
                                  className="beats-btn-purple-ctrl rounded-circle p-2"
                                  onClick={() => handlePlaySample(track)}
                                  title="Play sample"
                                >
                                  <FontAwesomeIcon icon={faPlay} />
                                </button>
                                <button 
                                  className="beats-btn-purple-ctrl rounded-circle p-2"
                                  style={{background: 'linear-gradient(to right, #6a11cb,rgb(116, 46, 173))'}}
                                  onClick={() => handleShowCreateProject(track)}
                                  title="Create project with this sample"
                                >
                                  <FontAwesomeIcon icon={faPlus} />
                                </button>
                              </div>
                            </ListGroup.Item>
                          ))}
                        </ListGroup>
                      </Card.Body>
                    </Card>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            
            {/* Right Pane - Recent Projects */}
            <Col md={6}>
              <Card className="beats-card h-100 d-flex flex-column">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <h5 className="mb-0" style={{color: '#d9d9d9'}}>Recent Projects</h5>
                    <Button 
                      as={Link}
                      to={`${url}/my-library`}
                      variant="link"
                      className="text-decoration-none"
                      style={{ color: 'var(--purple-accent)' }}
                    >
                      View All
                    </Button>
                  </div>
                  {recentProjects.length === 0 ? (
                    <div className="text-center py-4 bg-dark bg-opacity-25 rounded">
                      <FontAwesomeIcon 
                        icon={faFolderOpen} 
                        className="mb-3" 
                        style={{ fontSize: 'var(--font-3xl)', color: 'var(--text-muted)' }} 
                      />
                      <p className="text-muted mb-0">No recent projects found</p>
                      <small className="d-block mt-2 text-muted">Your latest work will appear here</small>
                    </div>
                  ) : (
                    <div className="recent-projects">
                      {recentProjects.map(project => (
                        <div 
                          key={project.id} 
                          className="beats-list-item p-3 d-flex align-items-center"
                          style={{ cursor: 'pointer' }}
                          onClick={() => history.push(`${url}/project/${project.id}`)}
                        >
                          <div className="me-3">
                            <FontAwesomeIcon 
                              icon={faMagic} 
                              style={{ color: 'var(--purple-accent)' }} 
                            />
                          </div>
                          <div className="flex-grow-1">
                            <h6 className="mb-0" style={{ color: '#d9d9d9' }}>{project.name}</h6>
                            <small className="text-muted">
                              Last edited: {new Date(project.modified_at).toLocaleDateString()}
                            </small>
                            <div className="mt-1">
                              {project.tags.map((tag, index) => (
                                <span 
                                  key={index}
                                  className="badge me-1"
                                  style={{ fontSize: 'var(--font-xs)', fontWeight: '400', backgroundColor: '#2589BD', color: 'white', opacity: 0.8 }}
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          </div>
                          <Button variant="link" className="p-0 text-light beats-btn-purple-ctrl">
                            <FontAwesomeIcon icon={faFolderOpen} />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Col>
      </Row>

      {/* Create Project Modal */}
      <Modal show={showCreateProject} onHide={resetProjectForm} centered size="lg">
        <Modal.Header className="bg-dark border-secondary">
          <Modal.Title style={{ color: '#d9d9d9' }}>Create New Project</Modal.Title>
          <Button 
            variant="link" 
            onClick={resetProjectForm}
            className="text-white p-0 ms-auto border-0"
            style={{ fontSize: 'var(--font-xl)' }}
          >
            <FontAwesomeIcon icon={faTimes} />
          </Button>
        </Modal.Header>
        <Modal.Body className="bg-dark">
          <Form>
            <Form.Group className="mb-3">
              <Form.Label style={{ color: '#d9d9d9' }}>Project Name *</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter project name..."
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                className="bg-secondary border-secondary text-white"
                style={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderColor: 'rgba(255, 255, 255, 0.2)',
                  color: 'white'
                }}
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label style={{ color: '#d9d9d9' }}>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                placeholder="Project description (optional)..."
                value={projectDescription}
                onChange={(e) => setProjectDescription(e.target.value)}
                className="bg-secondary border-secondary text-white"
                style={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderColor: 'rgba(255, 255, 255, 0.2)',
                  color: 'white'
                }}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label style={{ color: '#d9d9d9' }}>Tags/Labels</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter tags separated by commas (e.g., hip-hop, remix, demo)..."
                value={projectTags}
                onChange={(e) => setProjectTags(e.target.value)}
                className="bg-secondary border-secondary text-white"
                style={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderColor: 'rgba(255, 255, 255, 0.2)',
                  color: 'white'
                }}
              />
              <Form.Text className="text-muted">
                Tags help you organize and find your projects easily
              </Form.Text>
            </Form.Group>

            {(selectedFileName || selectedSampleTrack) && (
              <div className="mb-3">
                <div className="d-flex align-items-center p-3 rounded" style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)' }}>
                  <FontAwesomeIcon icon={faMusic} className="me-3" style={{ color: 'var(--purple-accent)' }} />
                  <div>
                    <p className="mb-0" style={{ color: '#d9d9d9' }}>
                      {selectedFileName || (selectedSampleTrack && selectedSampleTrack.name)}
                    </p>
                    <small style={{ color: 'rgba(217, 217, 217, 0.7)' }}>
                      {selectedSampleTrack ? 'Sample track' : 'Uploaded file'}
                    </small>
                  </div>
                </div>
              </div>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer className="bg-dark border-secondary">
          <Button 
            variant="outline-light" 
            onClick={resetProjectForm}
            className="beats-btn-outline"
            disabled={isCreatingProject}
          >
            Cancel
          </Button>
          <Button 
            className="beats-btn-purple"
            onClick={() => handleCreateProject()}
            disabled={!projectName.trim() || isCreatingProject}
          >
            {isCreatingProject ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Creating...
              </>
            ) : (
              'Create Project'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
    // </Container>
  );
};

export default BeatsHome; 